import { Sha256 } from "@aws-crypto/sha256-js";
import { ClientError, Variables } from "graphql-request";
import { fetchAuthSession } from "aws-amplify/auth";
import { HttpRequest } from "@smithy/protocol-http";
import { SignatureV4 } from "@smithy/signature-v4";
import axios from "axios";
import { getGraphqlClient } from "../graphql-client";

export type GqlAuthMode = "userPool" | "iam";

export interface GqlReqOptions<TVariables extends Variables = Variables> {
	query: string;
	variables?: TVariables;
	headers?: Record<string, string>;
	signal?: AbortSignal;
	authMode?: GqlAuthMode;
}

const APPSYNC_API_URL = process.env.APPSYNC_API_URL!;
const AWS_REGION = process.env.AWS_REGION ?? "us-east-1";
// eslint-disable-next-line @typescript-eslint/no-implied-eval
const importServerModule = new Function(
	"specifier",
	"return import(specifier);",
) as <TModule>(specifier: string) => Promise<TModule>;

interface ClientGraphqlResult<TQuery extends Record<string, any>> {
	data?: TQuery;
}

const unwrapGraphQlResult = <TQuery extends Record<string, any>>(
	result: TQuery,
): TQuery[keyof TQuery] => result[Object.keys(result)[0]!];

const getGraphqlErrorMessage = (error: unknown): string => {
	if (error instanceof ClientError) {
		return error.response.errors?.[0]?.message ?? "Something went wrong!";
	}

	if (
		error &&
		typeof error === "object" &&
		"errors" in error &&
		Array.isArray(error.errors) &&
		error.errors[0] &&
		typeof error.errors[0] === "object" &&
		"message" in error.errors[0]
	) {
		return String(error.errors[0].message);
	}

	if (error instanceof Error) {
		return error.message;
	}

	return "Something went wrong!";
};

const requestViaAxios = async <TQuery extends Record<string, any>>({
	query,
	variables,
	headers,
	signal,
}: Required<Pick<GqlReqOptions, "query" | "headers">> &
	Pick<GqlReqOptions, "variables" | "signal">): Promise<
	TQuery[keyof TQuery]
> => {
	const payload = await axios
		.post<{
			data?: TQuery;
			errors?: { message?: string }[];
		}>(
			APPSYNC_API_URL,
			{
				query,
				variables,
			},
			{
				headers: {
					...headers,
				},
				signal,
				// dont let axios throw errors so we can read errors payload
				validateStatus: () => true,
			},
		)
		.then((response) => response.data);

	if (payload.errors?.length) {
		throw new Error(payload.errors?.[0]?.message ?? "Something went wrong!");
	}

	if (!payload.data) {
		throw new Error("GraphQL response missing data");
	}

	return unwrapGraphQlResult(payload.data);
};

const requestWithServerIam = async <
	TQuery extends Record<string, any>,
	TVariables extends Variables = Variables,
>({
	query,
	variables,
	headers,
	signal,
}: GqlReqOptions<TVariables>): Promise<TQuery[keyof TQuery]> => {
	const url = new URL(APPSYNC_API_URL);
	const { defaultProvider } = await importServerModule<
		typeof import("@aws-sdk/credential-provider-node")
	>("@aws-sdk/credential-provider-node");
	const signer = new SignatureV4({
		credentials: defaultProvider(),
		region: AWS_REGION,
		service: "appsync",
		sha256: Sha256,
	});

	const body = JSON.stringify({ query, variables });
	const signedRequest = await signer.sign(
		new HttpRequest({
			method: "POST",
			protocol: url.protocol,
			path: url.pathname,
			hostname: url.hostname,
			headers: {
				"content-type": "application/json",
				host: url.hostname,
				...headers,
			},
			body,
		}),
	);

	return requestViaAxios<TQuery>({
		query,
		variables,
		signal,
		headers: Object.fromEntries(
			Object.entries(signedRequest.headers).filter(
				([, value]) => value !== undefined,
			),
		) as Record<string, string>,
	});
};

const getCurrentUserPoolToken = async () => {
	const session = await fetchAuthSession();

	return (
		session.tokens?.idToken?.toString() ??
		session.tokens?.accessToken?.toString() ??
		null
	);
};

export const gqlReq = async <
	TQuery extends Record<string, any>,
	TVariables extends Variables = Variables,
>({
	query,
	variables,
	headers,
	authMode,
	signal,
}: GqlReqOptions<TVariables>): Promise<TQuery[keyof TQuery]> => {
	try {
		if (typeof window === "undefined") {
			if (authMode === "userPool") {
				throw new Error("Failed to get token");
			}

			return requestWithServerIam<TQuery, TVariables>({
				query,
				variables,
				headers,
				signal,
				authMode,
			});
		}

		const resolvedAuthMode = authMode;
		if (resolvedAuthMode === "iam") {
			const result = (await (getGraphqlClient() as any).graphql(
				{
					query,
					variables: variables as Record<string, unknown> | undefined,
					authMode: "iam",
				},
				headers,
			)) as ClientGraphqlResult<TQuery>;
			return unwrapGraphQlResult(result.data!);
		}

		const token = await getCurrentUserPoolToken();
		if (!token) {
			if (resolvedAuthMode === "userPool") {
				throw new Error("Failed to get token");
			}

			const result = (await (getGraphqlClient() as any).graphql(
				{
					query,
					variables: variables as Record<string, unknown> | undefined,
					authMode: "iam",
				},
				headers,
			)) as ClientGraphqlResult<TQuery>;
			return unwrapGraphQlResult(result.data!);
		}

		const requestHeaders: Record<string, string> = {
			...headers,
			Authorization: `Bearer ${token}`,
		};

		return requestViaAxios<TQuery>({
			query,
			variables,
			signal,
			headers: requestHeaders,
		});
	} catch (error) {
		throw new Error(getGraphqlErrorMessage(error));
	}
};
